import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SwipeableDrawer,
  Switch,
  Typography
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import CloseRounded from "@mui/icons-material/CloseRounded";
import SavingsOutlined from "@mui/icons-material/SavingsOutlined";
import SkipNextRounded from "@mui/icons-material/SkipNextRounded";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import IosShareRounded from "@mui/icons-material/IosShareRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";

import type { Bot } from "@/data/mock-data";
import { desktopBottomDrawerPaperSx } from "@/theme/drawer-sx";

// ---- Types ----------------------------------------------------------------

interface BotActionsSheetProps {
  open: boolean;
  bot: Bot;
  /** Entitlement gate for the Share block (useCanShareBot). */
  canShare: boolean;
  /** Whether this bot is currently being watched on a paper account. */
  isWatched: boolean;
  /** Whether the bot's next rebalance is flagged to be skipped. */
  isSkipping: boolean;
  /**
   * Archived / draft bots can't trade — Go to cash + Skip trading are
   * disabled for them (Watch / Copy to drafts / Share still apply).
   */
  tradingDisabled: boolean;
  onClose: () => void;
  /** Liquidate the bot fully to cash (parent confirms via this sheet). */
  onGoToCash: () => void;
  /** Toggle the skip-next-rebalance flag. */
  onToggleSkip: () => void;
  /** Jump to the Structure tab to edit the bot's rules. */
  onEditStructure: () => void;
  /** Toggle paper-watching. */
  onToggleWatch: () => void;
  /** Save a draft copy of this bot. */
  onCopyToDrafts: () => void;
  /** Transient toast surfaced by the parent. */
  onToast: (msg: string) => void;
}

/** Build the (mock) shareable link for a bot. */
function shareLink(botId: string): string {
  return `https://app.whitehatbots.com/shared/${botId}`;
}

/** Copy text to the clipboard, swallowing the rare rejection. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// ---- Component ------------------------------------------------------------

/**
 * ⋯ actions bottom sheet for an owned bot (Phase 6, Sergey 2026-05-21).
 * Replaces the old dropdown menu — a bottom sheet matches the rest of
 * the bot drawer and gives room for the Share block (public toggle +
 * link button) below the action rows.
 *
 * Go to cash routes through an in-sheet confirm dialog — it liquidates
 * the whole position, so it shouldn't fire on a single mistaken tap.
 */
export function BotActionsSheet({
  open,
  bot,
  canShare,
  isWatched,
  isSkipping,
  tradingDisabled,
  onClose,
  onGoToCash,
  onToggleSkip,
  onEditStructure,
  onToggleWatch,
  onCopyToDrafts,
  onToast
}: BotActionsSheetProps) {
  const [confirmCashOpen, setConfirmCashOpen] = useState(false);
  // Public-vs-private share link. Local to the sheet — defaults to
  // private (most bots are not meant to be world-readable).
  const [isPublic, setIsPublic] = useState(false);

  // Each fresh open starts from a private link + closed confirm.
  useEffect(() => {
    if (open) {
      setIsPublic(false);
      setConfirmCashOpen(false);
    }
  }, [open, bot.id]);

  async function handleShareLink() {
    const ok = await copyToClipboard(shareLink(bot.id));
    onToast(
      ok
        ? `${isPublic ? "Public" : "Private"} link copied to clipboard.`
        : "Couldn't access the clipboard — copy the link manually."
    );
    onClose();
  }

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          ...desktopBottomDrawerPaperSx
        }
      }}
    >
      {/* Grab handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
        <Box
          sx={(t: Theme) => ({
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: t.palette.divider
          })}
        />
      </Box>

      {/* Close (×) */}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={(t: Theme) => ({
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          bgcolor: alpha(t.palette.text.primary, 0.08),
          "&:hover": { bgcolor: alpha(t.palette.text.primary, 0.16) }
        })}
      >
        <CloseRounded sx={{ fontSize: 22 }} />
      </IconButton>

      <Box sx={{ overflowY: "auto", pb: 1 }}>
        <List disablePadding sx={{ pt: 1 }}>
          {/* Go to cash */}
          <ListItemButton
            disabled={tradingDisabled}
            onClick={() => setConfirmCashOpen(true)}
            sx={{ py: 1.5, px: 2.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SavingsOutlined />
            </ListItemIcon>
            <ListItemText
              primary="Go to cash"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItemButton>
          <Divider component="li" />

          {/* Skip trading */}
          <ListItemButton
            disabled={tradingDisabled}
            onClick={() => {
              onToggleSkip();
              onClose();
            }}
            sx={{ py: 1.5, px: 2.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SkipNextRounded />
            </ListItemIcon>
            <ListItemText
              primary="Skip trading"
              secondary={
                isSkipping ? "Next rebalance will be skipped" : undefined
              }
              primaryTypographyProps={{ fontWeight: 600 }}
            />
            {isSkipping && (
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "warning.main" }}
              >
                ON
              </Typography>
            )}
          </ListItemButton>
          <Divider component="li" />

          {/* Edit structure */}
          <ListItemButton
            onClick={() => {
              onEditStructure();
              onClose();
            }}
            sx={{ py: 1.5, px: 2.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <AccountTreeOutlined />
            </ListItemIcon>
            <ListItemText
              primary="Edit algo"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItemButton>
          <Divider component="li" />

          {/* Watch / Unwatch */}
          <ListItemButton
            onClick={() => {
              onToggleWatch();
              onClose();
            }}
            sx={{ py: 1.5, px: 2.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {isWatched ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
            </ListItemIcon>
            <ListItemText
              primary={isWatched ? "Unwatch" : "Watch"}
              secondary={
                isWatched ? "Mirrored on a paper account" : "Track on paper"
              }
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItemButton>
          <Divider component="li" />

          {/* Copy to drafts */}
          <ListItemButton
            onClick={() => {
              onCopyToDrafts();
              onClose();
            }}
            sx={{ py: 1.5, px: 2.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ContentCopyRounded />
            </ListItemIcon>
            <ListItemText
              primary="Copy to drafts"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItemButton>

          {/* Share block — entitlement-gated */}
          {canShare && (
            <>
              <Divider component="li" />
              <ListItemButton
                onClick={handleShareLink}
                sx={{ py: 1.5, px: 2.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <IosShareRounded />
                </ListItemIcon>
                <ListItemText
                  primary="Share"
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItemButton>
            </>
          )}

          {/* View bot page — WHB-added escape hatch to the full page */}
          <Divider component="li" />
          <ListItemButton
            component={RouterLink}
            to={`/bots/${bot.id}`}
            onClick={onClose}
            sx={{ py: 1.5, px: 2.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <OpenInNewRounded />
            </ListItemIcon>
            <ListItemText
              primary="View bot page"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItemButton>
        </List>

        {/* Public toggle + share-link button */}
        {canShare && (
          <Box sx={{ px: 2.5, pt: 1.5, pb: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Public
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  Anyone with the link can view
                </Typography>
              </Box>
              <Switch
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
            </Stack>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<IosShareRounded sx={{ fontSize: 18 }} />}
              onClick={handleShareLink}
              sx={{ mt: 1.5, py: 1.1, fontWeight: 700, borderRadius: 2 }}
            >
              Share {isPublic ? "public" : "private"} link…
            </Button>
          </Box>
        )}
      </Box>

      {/* Go-to-cash confirmation */}
      <Dialog
        open={confirmCashOpen}
        onClose={() => setConfirmCashOpen(false)}
        sx={{ zIndex: (t) => t.zIndex.modal + 4 }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Move bot to cash?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This sells every holding in <strong>{bot.name}</strong> and parks
            the proceeds as cash. The bot stays set up — it resumes trading on
            its next rebalance unless you also pause or skip it.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirmCashOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setConfirmCashOpen(false);
              onGoToCash();
              onClose();
            }}
          >
            Go to cash
          </Button>
        </DialogActions>
      </Dialog>
    </SwipeableDrawer>
  );
}
